import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { CountryCode } from '@delmonicos/shared-design-system-react/src/components/country-code/country-code';
import { TextInput } from '@delmonicos/shared-design-system-react/src/components/input/text-input';
import { GeoMap } from '@delmonicos/shared-design-system-react/src/components/location/geo-map/geo-map.component';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { HeadingEnum } from '@delmonicos/shared-design-system-react/src/models/enums/heading.enum';
import { useUpdateLocationFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/location/update-location.form-validation';
import { UpdateLocationModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/location/update-location.model';
import { useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ConfigurationService } from '@app/core/configuration/configuration.service';
import countries from '@app/core/countries/countries';
import { LocationMapCoordinates } from '@app/feature/location/location-map-sync.util';
import {
  LocationMapFieldPaths,
  LocationMapFormSync,
  useLocationMapSync,
} from '@app/feature/location/use-location-map-sync';

import * as css from './location-update-form-step-2.module.scss';

type LocationUpdateMapFormValues = Pick<
  UpdateLocationModel,
  'locationId' | 'location'
>;

const UPDATE_LOCATION_MAP_FIELD_PATHS: LocationMapFieldPaths<LocationUpdateMapFormValues> =
  {
    address: 'location.address',
    city: 'location.city',
    postalCode: 'location.postalCode',
    country: 'location.country',
    latitude: 'location.coordinates.latitude',
    longitude: 'location.coordinates.longitude',
  };

// Pure form fields — no map context dependency
const LocationUpdateFormStep2AddressCard = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { control } = useFormContext<LocationUpdateMapFormValues>();
  const {
    locationAddress,
    locationCity,
    locationPostalCode,
    locationCountry,
    locationCoordinatesLatitude,
    locationCoordinatesLongitude,
  } = useUpdateLocationFormValidation();

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Card gap={GapEnum.M} flex={1}>
      <AccentTitle
        icon="marker-pin-04"
        text={t('$.location-update.step-2.localization.title')}
        as={HeadingEnum.H2}
      />
      <Controller
        name="location.address"
        control={control}
        rules={locationAddress}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-location.address')}
            placeholder={t('delmonicos-shared-sdk.outputs-location.address')}
            required={!!locationAddress.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) =>
              field.onChange(e.target.value === '' ? '' : e.target.value)
            }
          />
        )}
      />
      <Stack gap={GapEnum.M} flexWrap="wrap">
        <Controller
          name="location.postalCode"
          control={control}
          rules={locationPostalCode}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-location.postal-code')}
              placeholder={t(
                'delmonicos-shared-sdk.outputs-location.postal-code',
              )}
              required={!!locationPostalCode.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : e.target.value)
              }
            />
          )}
        />
        <Controller
          name="location.city"
          control={control}
          rules={locationCity}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-location.city')}
              placeholder={t('delmonicos-shared-sdk.outputs-location.city')}
              required={!!locationCity.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : e.target.value)
              }
            />
          )}
        />
      </Stack>

      <Controller
        name="location.country"
        control={control}
        rules={locationCountry}
        render={({ field, fieldState: { error } }) => (
          <CountryCode
            errorMessage={error?.message}
            fieldValue={field.value}
            fieldOnChange={field.onChange}
          />
        )}
      />

      <Stack gap={GapEnum.S} flexWrap="wrap">
        <Controller
          name="location.coordinates.latitude"
          control={control}
          rules={locationCoordinatesLatitude}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-location.latitude')}
              placeholder={t('delmonicos-shared-sdk.outputs-location.latitude')}
              required={!!locationCoordinatesLatitude}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : e.target.value)
              }
            />
          )}
        />
        <Controller
          name="location.coordinates.longitude"
          control={control}
          rules={locationCoordinatesLongitude}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-location.longitude')}
              placeholder={t(
                'delmonicos-shared-sdk.outputs-location.longitude',
              )}
              required={!!locationCoordinatesLongitude}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : e.target.value)
              }
            />
          )}
        />
      </Stack>
    </Card>
  );
};

const LocationUpdateFormStep2LocalizationFragment = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { getValues } = useFormContext<LocationUpdateMapFormValues>();

  const formValues = getValues();

  // Captured once, at mount — this must stay the location's original data
  // for the whole session. The SDK compares live coordinates against this
  // to detect "back at the starting point"; if it were recomputed from
  // getValues() on every render (as it used to be), it would silently catch
  // up to whatever the user just typed, the comparison would trivially
  // become true, and the SDK would skip reverse-geocoding entirely — moving
  // the pin but never updating the address fields or the map's own address
  // input.
  const initialAddressRef = useRef<{
    address: string;
    city: string;
    postalCode: string;
    country: string;
    coordinates: LocationMapCoordinates;
  }>();
  if (!initialAddressRef.current) {
    initialAddressRef.current = {
      address: formValues.location.address || '',
      city: formValues.location.city || '',
      postalCode: formValues.location.postalCode || '',
      country: formValues.location.country
        ? (countries.alpha3ToAlpha2(formValues.location.country) ?? '')
        : '',
      coordinates: {
        latitude: Number(formValues.location.coordinates?.latitude ?? 0),
        longitude: Number(formValues.location.coordinates?.longitude ?? 0),
      },
    };
  }
  const initialAddress = initialAddressRef.current;

  // HANDLERS — map → form / form → map //////////////////////////////////////

  const { addressGuard, coordinatesGuard, handleAddressChange } =
    useLocationMapSync(UPDATE_LOCATION_MAP_FIELD_PATHS);

  // RENDER ////////////////////////////////////////////////////////////////////

  const apiKey = ConfigurationService.getGoogleApiKey();

  if (!apiKey) {
    return (
      <Stack gap={GapEnum.L} flex={1}>
        <LocationUpdateFormStep2AddressCard />
      </Stack>
    );
  }

  return (
    <Stack gap={GapEnum.L} flex={1}>
      <LocationUpdateFormStep2AddressCard />
      <Stack flex={1} className={css.mapContainer}>
        <GeoMap
          interactive
          apiKey={apiKey}
          mapId="update-location-map"
          zoom={16}
          initialAddress={initialAddress}
          onAddressChange={handleAddressChange}>
          <LocationMapFormSync
            fieldPaths={UPDATE_LOCATION_MAP_FIELD_PATHS}
            addressGuard={addressGuard}
            coordinatesGuard={coordinatesGuard}
          />
        </GeoMap>
      </Stack>
    </Stack>
  );
};

export const LocationUpdateFormStep2Controller = (): React.JSX.Element => {
  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Stack gap={GapEnum.L} direction="column" flex={1} alignItems="stretch">
      <LocationUpdateFormStep2LocalizationFragment />
    </Stack>
  );
};
